'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Sunrise, Sunset } from 'lucide-react';
import { formatCurrency, formatPct, cn } from '@/lib/utils';
import { betaTier, formatBetaTooltip, hasBeta, BETA_TIER_TEXT } from '@/lib/beta';
import { getStocksBatch, getStockHistory, type OHLCV } from '@/lib/api-client';
import { getETDate, addEtDays } from '@/lib/marketTime';
import { MiniChart } from '@/components/charts/MiniChart';
import PopupChart from '@/components/charts/PopupChart';
import WatchButton from '@/components/watchlist/WatchButton';
import { TickerBadges } from '@/components/ticker/TickerBadges';
import { EtfBadge } from '@/components/ticker/EtfBadge';
import { tickerHref, rememberTickerType } from '@/lib/tickerTypeCache';

// localStorage keys for the per-session toggles. Persisted across the
// entire popup surface so the user's preference (typically: both ON,
// hiding only PM noise on illiquid names) sticks across navigations.
const SHOW_PM_KEY = 'tickerPopup:showPM';
const SHOW_AH_KEY = 'tickerPopup:showAH';

function readBoolSetting(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = window.localStorage.getItem(key);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch { /* private mode / quota — ignore */ }
  return fallback;
}

function writeBoolSetting(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(key, value ? '1' : '0'); } catch { /* ignore */ }
}

/** ET wall-clock minute-of-day from a "YYYY-MM-DD HH:MM" intraday bar
 *  date. Used to bucket bars into pre-market / RTH / after-hours so
 *  the PM/AH toggles can include or filter by session. Daily bars
 *  (no time component) are treated as RTH. */
function intradaySession(date: string): 'pre' | 'regular' | 'after' | 'unknown' {
  if (!date.includes(' ')) return 'unknown';
  const m = date.match(/(\d{2}):(\d{2})$/);
  if (!m) return 'unknown';
  const mm = Number(m[1]) * 60 + Number(m[2]);
  if (!Number.isFinite(mm)) return 'unknown';
  if (mm < 9 * 60 + 30) return 'pre';
  if (mm > 16 * 60) return 'after';
  return 'regular';
}

export interface TickerPopupProps {
  ticker: string;
  /** Anchor rect (in viewport coords) used to position the popup. */
  anchor: DOMRect;
  onClose: () => void;
  /** Optional hover handlers — when the popup was opened on hover, the
   *  provider wires these so the popup stays open while the cursor is over
   *  it (cancel the pending close) and closes after leaving. Unset for
   *  click-opened popups, which persist regardless of pointer position. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Bumped from 300x220 / 64-tall chart so the chart has more room to breathe
// and matches the live-grid tile aesthetic operators are now used to.
const POPUP_W = 360;
const POPUP_H = 310;
// PopupChart needs room for the X-axis date labels (~22px) and the
// hover tooltip strip at the top (~4px), so a bit more vertical
// headroom than MiniChart's previous 110.
const SPARK_H = 140;

type Timeframe = '1D' | '1W' | '1M' | '3M' | '1Y' | '5Y' | 'ALL';
const TIMEFRAMES: Timeframe[] = ['1D', '1W', '1M', '3M', '1Y', '5Y', 'ALL'];
/** Fetch window per timeframe. ALL matches StockChart's "All" preset (3650d).
 *  Note this drives the FETCH; the calendar clip below deliberately skips ALL,
 *  which must show every bar it gets back rather than trimming to a window. */
const TF_DAYS: Record<Exclude<Timeframe, '1D'>, number> = {
  '1W': 7, '1M': 30, '3M': 90, '1Y': 365, '5Y': 1825, 'ALL': 3650,
};

/** Empty per-timeframe bar cache. Derived from TIMEFRAMES rather than written
 *  out twice (init + ticker reset), so adding a timeframe cannot leave a key
 *  missing from one of them. */
const emptySeries = (): Record<Timeframe, OHLCV[] | null | undefined> =>
  Object.fromEntries(TIMEFRAMES.map((t) => [t, undefined])) as Record<Timeframe, OHLCV[] | null | undefined>;

interface Quote {
  price: number;
  change: number;
  changePct: number;
  name: string;
  rsRating: number | null;
  rsi14: number | null;
  beta: number | null;
  // tickers.type from /stocks/batch. Funds swap the beta slot for an ETF
  // chip (beta is a single-stock exposure number) and link straight to
  // /etf/{X} instead of bouncing through the middleware redirect.
  type: 'stock' | 'etf' | null;
  // Extended-hours quote — populated by priceSync during pre-market
  // (4:00-9:30 ET) and after-hours (16:00-20:00 ET). When the current
  // session is one of those AND the values are present, the popup
  // renders a small PM/AH pill alongside the regular change indicator
  // (mirrors the watchlist tile pattern).
  extendedPrice: number | null;
  extendedChange: number | null;
  extendedChangePct: number | null;
  // The API can also return 'closed' overnight (post-AH, pre-PM) and
  // on weekends — the published StockDetail type was incomplete.
  // Capture it so we can still surface the most recent extended quote
  // during the closed window.
  marketSession: 'regular' | 'pre-market' | 'after-hours' | 'closed' | null;
}

/** Fixed-position popup showing a ticker's name, price, day change ($/%) and
 *  a selectable-timeframe sparkline. Drives every dashboard widget's ticker
 *  click via TickerPopupProvider. */
export default function TickerPopup({ ticker, anchor, onClose, onMouseEnter, onMouseLeave }: TickerPopupProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [tf, setTf] = useState<Timeframe>('1D');
  // Per-session display toggles. Default ON — extended-hours volume is
  // increasingly meaningful (gap-and-go names, earnings reactions). User
  // can disable for noisy illiquid tickers. Persisted in localStorage so
  // the choice sticks across popup sessions.
  const [showPM, setShowPM] = useState<boolean>(() => readBoolSetting(SHOW_PM_KEY, true));
  const [showAH, setShowAH] = useState<boolean>(() => readBoolSetting(SHOW_AH_KEY, true));
  useEffect(() => { writeBoolSetting(SHOW_PM_KEY, showPM); }, [showPM]);
  useEffect(() => { writeBoolSetting(SHOW_AH_KEY, showAH); }, [showAH]);
  // Per-timeframe bars. `undefined` = not loaded yet, `null` = load
  // failed / no data, `OHLCV[]` = loaded. Bars (not just closes) so we
  // can calendar-day-filter and so MiniChart can classify each bar's
  // session (PM / regular / AH) for the gray-segment rendering.
  const [series, setSeries] = useState<Record<Timeframe, OHLCV[] | null | undefined>>(emptySeries);

  // Position relative to the anchor; flip above when there's no space below.
  // Stored in state so drag can update it.
  const [pos, setPos] = useState(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
    let x = anchor.left + anchor.width / 2 - POPUP_W / 2;
    x = Math.max(8, Math.min(vw - POPUP_W - 8, x));
    let y = anchor.bottom + 6;
    if (y + POPUP_H > vh - 8) y = Math.max(8, anchor.top - POPUP_H - 6);
    return { x, y };
  });

  // Drag state. Bound on pointer events (not just mouse) so touch on
  // mobile drives the same drag flow — phone users can reposition the
  // popup so it doesn't cover the row they tapped from. While dragging
  // is active we bind move/up handlers on document so the finger can
  // leave the popup itself.
  const dragRef = useRef<{ dx: number; dy: number; pointerId: number } | null>(null);
  const startDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Only the empty header chrome initiates a drag — interactive
    // children (close X button, ticker-name link) skip the handler so a
    // click on them works normally. Without the `closest('a')` guard
    // the new ticker-name link couldn't be clicked because pointerdown
    // on it would arm the drag and the subsequent click was treated
    // as the drag end.
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    // Don't preventDefault on touch — that would block scrolling for
    // touches that don't end up dragging. Capture the pointer instead so
    // subsequent move/up events fire even if the finger drifts off.
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, pointerId: e.pointerId };
    const onMove = (ev: PointerEvent): void => {
      if (!dragRef.current || ev.pointerId !== dragRef.current.pointerId) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const x = Math.max(8, Math.min(vw - POPUP_W - 8, ev.clientX - dragRef.current.dx));
      const y = Math.max(8, Math.min(vh - 80, ev.clientY - dragRef.current.dy));
      setPos({ x, y });
    };
    const onUp = (ev: PointerEvent): void => {
      if (!dragRef.current || ev.pointerId !== dragRef.current.pointerId) return;
      dragRef.current = null;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  // Quote refreshes when ticker changes (popup may stay mounted across
  // ticker switches via the TickerLink data-attribute opt-out below).
  // Reset quote to null so the loading state shows briefly while the
  // new ticker's data fetches, instead of flashing the previous
  // ticker's stale price.
  useEffect(() => {
    let cancelled = false;
    setQuote(null);
    getStocksBatch([ticker]).then((res) => {
      if (cancelled) return;
      const data = res[0]?.data;
      if (data) setQuote({
        price: data.price, change: data.change, changePct: data.changePct, name: data.name,
        rsRating: data.rsRating ?? null, rsi14: data.rsi14 ?? null, beta: data.beta ?? null,
        type: data.type ?? null,
        extendedPrice: data.extendedHoursPrice ?? null,
        extendedChange: data.extendedHoursChange ?? null,
        extendedChangePct: data.extendedHoursChangePct ?? null,
        marketSession: data.marketSession ?? null,
      });
      // Seed the shared type cache so every TickerLink for this symbol
      // routes to the canonical detail page from now on.
      if (data?.type) rememberTickerType(ticker, data.type);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [ticker]);

  // Reset per-timeframe series when the ticker changes — otherwise the
  // popup would show the previous ticker's chart while the new one
  // loads, since the keyed-by-tf state survives across tickers.
  useEffect(() => {
    setSeries(emptySeries());
  }, [ticker]);

  // Lazy-load the active timeframe.
  useEffect(() => {
    if (series[tf] !== undefined) return;
    let cancelled = false;
    if (tf === '1D') {
      // Switched from getHeatmapSparklines (closes only, no dates) to
      // getStockHistory intraday so MiniChart gets full OHLCV bars +
      // ET wall-clock dates needed to classify each bar's session and
      // render PM/AH segments in gray (matching the live-grid tile).
      getStockHistory(ticker, 1, 'intraday', true).then((bars) => {
        if (cancelled) return;
        const usable = bars.filter((b) => Number.isFinite(b.close) && b.close > 0 && typeof b.date === 'string');
        setSeries((prev) => ({ ...prev, '1D': usable.length > 1 ? usable : null }));
      }).catch(() => setSeries((prev) => ({ ...prev, '1D': null })));
    } else if (tf === '1W') {
      // Mirror StockChart: 1W is INTRADAY (5-min bars over 7 days), not
      // daily. Fetch WITH extended-hours so the PM/AH toggles can fold
      // them in/out client-side without a refetch on toggle change.
      // Previously fetched with extended=false; toggling on PM/AH on 1W
      // had no effect because those bars never arrived.
      getStockHistory(ticker, 7, 'intraday', true).then((bars) => {
        if (cancelled) return;
        const usable = bars.filter((b) => Number.isFinite(b.close) && b.close > 0 && typeof b.date === 'string');
        setSeries((prev) => ({ ...prev, '1W': usable.length > 1 ? usable : null }));
      }).catch(() => setSeries((prev) => ({ ...prev, '1W': null })));
    } else {
      getStockHistory(ticker, TF_DAYS[tf]).then((bars) => {
        if (cancelled) return;
        const usable = bars.filter((b) => Number.isFinite(b.close) && typeof b.date === 'string');
        setSeries((prev) => ({ ...prev, [tf]: usable.length > 1 ? usable : null }));
      }).catch(() => setSeries((prev) => ({ ...prev, [tf]: null })));
    }
    return () => { cancelled = true; };
  }, [ticker, tf, series]);

  // Close on Escape only. Click-outside intentionally does NOT close —
  // operators want to compare the popup against tiles behind it without
  // it disappearing the moment they click a different cell. The X
  // button (and Escape) are the explicit ways out.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Calendar-day-clip the loaded bars so each timeframe's start price
  // lines up with the requested window. The /history endpoint returns the
  // last N TRADING days when servicing days=N from a longer cache slice
  // — so a "1Y" pull can include ~1.4 calendar years of bars and shows a
  // wrong start price unless we trim here. (Matches StockChart's
  // filterByDays behavior.)
  const bars = series[tf];
  const clippedBars = useMemo<OHLCV[] | null>(() => {
    if (!bars || bars.length === 0) return null;
    // Apply the PM/AH toggles on intraday timeframes only — daily bars
    // (1M / 3M / 1Y / 5Y) have no time component to bucket on, so the
    // toggles don't apply.
    const isIntraday = tf === '1D' || tf === '1W';
    let working = bars;
    if (isIntraday && (!showPM || !showAH)) {
      working = bars.filter((b) => {
        const sess = intradaySession(b.date);
        if (sess === 'pre' && !showPM) return false;
        if (sess === 'after' && !showAH) return false;
        return true;
      });
    }
    if (tf === '1D') return working.length > 1 ? working : null;
    // ALL renders every bar the fetch returned. Clipping it to the TF_DAYS
    // window would silently drop older history for long-listed names, which
    // is the opposite of what the range promises.
    if (tf === 'ALL') return working.length > 1 ? working : null;
    // Compare ET date strings (bar dates are ET-anchored "YYYY-MM-DD[ HH:MM]")
    // rather than a browser-local cutoff vs locally-parsed bar dates.
    const cutoffYmd = addEtDays(getETDate(new Date()), -TF_DAYS[tf as Exclude<Timeframe, '1D'>]);
    const filtered = working.filter((b) => b.date.slice(0, 10) >= cutoffYmd);
    return filtered.length > 1 ? filtered : null;
  }, [bars, tf, showPM, showAH]);
  // For the 1D color we anchor on prev_close (matches the headline change %
  // and the live-grid tile's color logic). For longer timeframes we use the
  // first-to-last RTH-only close — same convention StockChart uses.
  const prevClose1D = useMemo(() => {
    if (!quote) return null;
    const pc = quote.price - quote.change;
    return Number.isFinite(pc) && pc > 0 ? pc : null;
  }, [quote]);
  const periodChange = useMemo(() => {
    if (!clippedBars || clippedBars.length < 2) return null;
    const first = clippedBars[0].close;
    const last = quote && quote.price > 0 ? quote.price : clippedBars[clippedBars.length - 1].close;
    if (first <= 0) return null;
    return { abs: last - first, pct: ((last - first) / first) * 100 };
  }, [clippedBars, quote]);
  // PM / AH only bucket on the intraday timeframes; 1M+ are daily bars.
  const sessionsApply = tf === '1D' || tf === '1W';
  const isUp = tf === '1D'
    ? (quote ? quote.changePct >= 0 : true)
    : (periodChange ? periodChange.pct >= 0 : true);

  return (
    <div
      ref={ref}
      role="dialog"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ position: 'fixed', left: pos.x, top: pos.y, width: POPUP_W, zIndex: 70 }}
      className="bg-surface border border-default rounded-lg shadow-2xl overflow-hidden"
    >
      <div
        onPointerDown={startDrag}
        // touch-action:none on the drag chrome so iOS/Android don't
        // hijack the gesture for scroll. Children (close button, link)
        // re-enable touch via their own event handlers.
        style={{ touchAction: 'none' }}
        className="flex items-center justify-between px-3 py-2 border-b border-default cursor-move select-none"
      >
        <div className="flex items-center gap-2 min-w-0">
          <a
            href={tickerHref(ticker, quote?.type ?? undefined)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-2 py-0.5 rounded-lg text-sm font-mono font-bold text-sky-300 bg-sky-500/15 border border-sky-500/30 hover:bg-sky-500/25 hover:text-sky-200 transition-colors cursor-pointer"
            title={`Open ${ticker} in a new tab`}
          >
            {ticker}
          </a>
          {quote && <span className="text-[10px] text-fg-muted truncate">{quote.name}</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <WatchButton ticker={ticker} variant="icon" alwaysVisible />
          <button
            type="button"
            onClick={onClose}
            className="text-fg-subtle hover:text-fg"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="px-3 py-2">
        {quote ? (() => {
          // PM / AH micro-pill — surfaces the extended-hours move on
          // top of the regular session change. Renders whenever
          // priceSync has captured an extended quote AND we're not in
          // the regular session itself. Covers pre-market, after-hours,
          // AND the "closed" overnight/weekend window (the API returns
          // extendedPrice from the prior session's last extended bar
          // even when the market is closed, so the pill stays
          // informative until the next regular open clears it).
          let extPill: React.ReactNode = null;
          {
            const sess = quote.marketSession;
            const haveExt = quote.extendedPrice != null && quote.extendedChangePct != null;
            if (sess !== 'regular' && haveExt) {
              const extUp = (quote.extendedChangePct ?? 0) >= 0;
              const isPM = sess === 'pre-market';
              const Icon = isPM ? Sunrise : Sunset;
              const label = isPM ? 'PM' : 'AH';
              const tip = isPM
                ? 'Pre-market quote on top of the regular-session change'
                : sess === 'after-hours'
                  ? 'After-hours quote on top of the regular-session change'
                  : 'Most recent after-hours quote (market closed)';
              const ext = quote.extendedChange ?? 0;
              extPill = (
                <span
                  title={tip}
                  className={cn(
                    'inline-flex items-center gap-1 text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded border',
                    extUp
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                      : 'bg-red-500/10 border-red-500/20 text-red-400',
                  )}
                >
                  <Icon size={10} className={extUp ? 'text-emerald-400' : 'text-red-400'} />
                  <span className="text-fg-subtle">{label}</span>
                  <span className="text-white">{formatCurrency(quote.extendedPrice as number)}</span>
                  <span>{ext >= 0 ? '+' : ''}{formatCurrency(ext)}</span>
                  <span>{formatPct(quote.extendedChangePct as number)}</span>
                </span>
              );
            }
          }
          const isEtf = quote.type === 'etf';
          // Beta is a single-stock exposure reading: hidden for funds (the
          // ETF chip takes its slot) and for the API's null-coerced 0.
          const beta = !isEtf && hasBeta(quote.beta) ? quote.beta : null;
          const haveRsOrRsi = quote.rsRating != null || quote.rsi14 != null || beta != null || isEtf;
          // The regular-session change and the RS / RSI pair trade places:
          // change now sits directly under the price it belongs to (reading
          // as one price block), and RS / RSI — reference numbers, not the
          // headline — move to the far end of the price line.
          const changeText = (
            <span className={`text-xs font-mono flex-shrink-0 ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
              {tf === '1D' ? (
                <>
                  {quote.change >= 0 ? '+' : ''}{formatCurrency(quote.change)}
                  <span className="ml-1">{formatPct(quote.changePct)}</span>
                </>
              ) : periodChange ? (
                <>
                  {periodChange.abs >= 0 ? '+' : ''}{formatCurrency(periodChange.abs)}
                  <span className="ml-1">{formatPct(periodChange.pct)}</span>
                </>
              ) : (
                <span className="text-fg-subtle">—</span>
              )}
            </span>
          );
          const rsRsi = haveRsOrRsi ? (
            <div className="flex items-center gap-3 text-[10px] font-mono text-fg-muted flex-shrink-0">
              {quote.rsRating != null && (
                <span title="IBD-style relative strength — 1-99 percentile of trailing-year price action vs the universe">
                  <span className="text-fg-subtle">RS</span>{' '}
                  <span className={cn('font-semibold',
                    quote.rsRating >= 80 ? 'text-emerald-400' :
                    quote.rsRating >= 50 ? 'text-fg' :
                    'text-rose-400')}>{quote.rsRating}</span>
                </span>
              )}
              {quote.rsi14 != null && (
                <span title="Wilder's RSI(14) — momentum oscillator. >70 overbought, <30 oversold">
                  <span className="text-fg-subtle">RSI</span>{' '}
                  <span className={cn('font-semibold',
                    quote.rsi14 >= 70 ? 'text-rose-400' :
                    quote.rsi14 <= 30 ? 'text-emerald-400' :
                    'text-fg')}>{quote.rsi14}</span>
                </span>
              )}
              {/* Beta sits with RS and RSI because it answers the same kind of
                  question — how does this name behave — and shares their
                  colour-coded-number shape. Its palette is the cool→warm risk
                  gradient from lib/beta, NOT the red/green used for RS and
                  RSI: high beta is more exposure, not a bearish reading. */}
              {beta != null && (
                <span title={formatBetaTooltip(beta)} className="cursor-help">
                  <span className="text-fg-subtle">β</span>{' '}
                  <span className={cn('font-semibold', BETA_TIER_TEXT[betaTier(beta) ?? 'market'])}>
                    {beta.toFixed(2)}
                  </span>
                </span>
              )}
              {/* Funds: the beta slot carries the ETF chip instead. */}
              {isEtf && (
                <EtfBadge title="Exchange-traded fund — beta is a single-stock reading and is not shown for funds" />
              )}
            </div>
          ) : null;

          return (
            <>
              {/* Row 1: price + status badges on the left, RS / RSI on the
                  right. flex-wrap so RS / RSI wraps below rather than clipping
                  when a high price + several badges fill the 360px popup. */}
              <div className="flex flex-wrap items-center justify-between mb-1 gap-x-2 gap-y-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  {/* Replaces the plain white text price with the same
                      colored-background pill MiniChart used to render
                      inside the chart — single, prominent, direction-aware
                      price callout. */}
                  <span className={cn(
                    'text-xl font-mono font-semibold tabular-nums',
                    isUp ? 'text-emerald-400' : 'text-red-400',
                  )}>
                    {formatCurrency(quote.price)}
                  </span>
                  {/* Earnings / D / N / price-alert status badges, next to the price.
                      N's headline popover renders in a portal so the popup's
                      overflow-hidden doesn't clip it. */}
                  <TickerBadges ticker={ticker} />
                </div>
                {rsRsi ?? <span />}
              </div>
              {/* Row 2: the session change on the left, AH/PM micro-pill on
                  the right. Sharing a row keeps the popup compact — the pill
                  was once stacked on its own line and pushed the chart down.
                  Always rendered: the change is never absent, so the popup's
                  height no longer depends on whether RS / RSI happen to
                  exist for this ticker. */}
              <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                {changeText}
                {extPill}
              </div>
            </>
          );
        })() : (
          // Height-matched skeleton, NOT a shorter placeholder: the loaded
          // state is two rows, so collapsing to a bare spacer and expanding
          // again is what made the popup jump on every ticker switch.
          <div aria-busy="true" aria-label="Loading quote">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="skeleton h-7 w-24 rounded" />
              <div className="skeleton h-4 w-24 rounded" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="skeleton h-4 w-20 rounded" />
              <div className="skeleton h-5 w-36 rounded" />
            </div>
          </div>
        )}

        <div
          className="bg-card-bg rounded overflow-hidden border border-default flex flex-col"
          style={{ height: SPARK_H }}
        >
          {clippedBars ? (
            // PopupChart on 1W+ for the Google-Finance-style timeline
            // axes + hover tooltip. MiniChart stays on 1D so we keep
            // the PM/AH session-color distinction (gray non-RTH
            // segments) which matters on intraday but not on daily.
            tf === '1D' ? (
              <MiniChart
                bars={clippedBars}
                isUp={isUp}
                prevClose={prevClose1D}
                livePrice={quote?.price ?? null}
                showFooter
                showHover
                className="flex-1"
              />
            ) : (
              <PopupChart
                bars={clippedBars}
                isUp={isUp}
                className="flex-1"
                // Axis off so 1W+ matches the axis-free 1D MiniChart beside
                // it — no price gutter, no horizontal grid lines.
                showAxis={false}
              />
            )
          ) : (
            bars === undefined ? (
              <div
                className="skeleton flex-1 m-2 rounded"
                aria-busy="true"
                aria-label={`Loading ${tf} chart`}
              />
            ) : (
              <div className="text-[10px] text-fg-subtle text-center flex-1 flex items-center justify-center">
                No data for this range
              </div>
            )
          )}
        </div>

        {/* Controls sit below the chart. The session toggles live on the same
            row, past a divider and in violet rather than the timeframes' sky,
            so it reads as a different KIND of control — one filters which
            sessions are drawn, the others pick the window. They are disabled
            (not hidden) beyond 1W: pre/after-hours bucketing has no meaning on
            daily bars, and hiding them would make the row jump width as you
            move between timeframes. */}
        <div className="mt-2 flex items-center gap-1">
          {TIMEFRAMES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTf(t)}
              className={cn(
                'flex-1 text-[10px] font-mono py-1 rounded transition-colors',
                tf === t
                  ? 'bg-sky-500/20 text-sky-300'
                  : 'text-fg-subtle hover:text-fg hover:bg-slate-700/40',
              )}
            >
              {t}
            </button>
          ))}
          <span className="w-px self-stretch bg-border-default mx-0.5" aria-hidden />
          {([['PM', showPM, setShowPM, 'pre-market bars (4:00–9:30 ET)'],
             ['AH', showAH, setShowAH, 'after-hours bars (16:00–20:00 ET)']] as const).map(
            ([label, on, set, what]) => (
              <button
                key={label}
                type="button"
                disabled={!sessionsApply}
                onClick={() => set((v) => !v)}
                title={sessionsApply ? `Toggle ${what}` : 'Sessions apply to 1D and 1W only'}
                className={cn(
                  'px-1.5 py-1 rounded text-[10px] font-mono transition-colors',
                  !sessionsApply
                    ? 'text-fg-subtle/30 cursor-not-allowed'
                    : on
                      ? 'bg-violet-500/20 text-violet-300'
                      : 'text-fg-subtle hover:text-fg hover:bg-slate-700/40',
                )}
              >
                {label}
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
